import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { UserTypeOption, UpdateIncidentInput } from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import { Col, Row } from 'react-grid-system';
import { theme } from '@/styles';
import { Button } from '../atoms';
import { NormalFormNumberField } from '../molecules';

const ChartContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-box1);
`;

const ColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ $color }) => $color};
  border-radius: 50%;
  margin-right: 8px;
`;

const PieContainer = styled.div`
  position: relative;
  height: 250px;
  width: 100%;
`;

const PercentageNumber = styled.div`
  position: absolute;
  font-size: 28px;
  font-weight: 700;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${theme.colors.dark};
`;

const CenterLabel = styled.div`
  position: absolute;
  top: 62%;
  left: 50%;
  transform: translate(-50%, 0);
  font-size: 14px;
  color: #666;
  text-align: center;
`;

const COLORS = [
  theme.colors.primary1,
  theme.colors.primary2,
  theme.colors.secondary1,
  theme.colors.secondary2,
];

const userTypeMapper = {
  [UserTypeOption.Tenant]: { en: 'Tenant', es: 'Inquilino' },
  [UserTypeOption.Owner]: { en: 'Owner', es: 'Propietario' },
  [UserTypeOption.Manager]: { en: 'Manager', es: 'Administrador' },
  [UserTypeOption.Supplier]: { en: 'Supplier', es: 'Proveedor' },
};

type IncidentResponsibilityManagerProps = {
  initialResponsibilities: {
    [UserTypeOption.Tenant]: number;
    [UserTypeOption.Owner]: number;
    [UserTypeOption.Manager]: number;
    [UserTypeOption.Supplier]: number;
  };
  onUpdate: (data: Partial<UpdateIncidentInput>) => void;
  isLoading?: boolean;
};

export const IncidentResponsibilityManager = ({
  initialResponsibilities,
  onUpdate,
  isLoading = false,
}: IncidentResponsibilityManagerProps) => {
  const userLanguage = useUserLanguage();
  const [responsibilities, setResponsibilities] = useState(initialResponsibilities);
  const [error, setError] = useState('');

  const total = useMemo(() => {
    return Object.values(responsibilities).reduce((acc, val) => acc + val, 0);
  }, [responsibilities]);

  useEffect(() => {
    if (total !== 100) {
      setError(userLanguage === 'es' 
        ? `La suma debe ser 100%. Actual: ${total}%` 
        : `Total must be 100%. Current: ${total}%`);
    } else {
      setError('');
    }
  }, [total, userLanguage]);

  const handleChange = (type: UserTypeOption, value: string | number | null) => {
    const numValue = Number(value ?? 0);
    setResponsibilities(prev => ({
      ...prev,
      [type]: numValue,
    }));
  };

  const chartData = [
    { name: userTypeMapper[UserTypeOption.Tenant][userLanguage], value: responsibilities[UserTypeOption.Tenant], color: COLORS[0], type: UserTypeOption.Tenant },
    { name: userTypeMapper[UserTypeOption.Owner][userLanguage], value: responsibilities[UserTypeOption.Owner], color: COLORS[1], type: UserTypeOption.Owner },
    { name: userTypeMapper[UserTypeOption.Manager][userLanguage], value: responsibilities[UserTypeOption.Manager], color: COLORS[2], type: UserTypeOption.Manager },
    { name: userTypeMapper[UserTypeOption.Supplier][userLanguage], value: responsibilities[UserTypeOption.Supplier], color: COLORS[3], type: UserTypeOption.Supplier },
  ].filter(d => d.value > 0);

  const handleUpdate = () => {
    if (total === 100) {
      onUpdate({
        tenantPaymentResponsability: responsibilities[UserTypeOption.Tenant].toString(),
        ownerPaymentResponsability: responsibilities[UserTypeOption.Owner].toString(),
        managerPaymentResponsability: responsibilities[UserTypeOption.Manager].toString(),
        supplierPaymentResponsability: responsibilities[UserTypeOption.Supplier].toString(),
      });
    }
  };

  return (
    <ChartContainer>
      <Row align="center">
        <Col lg={5}>
          <PieContainer>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  innerRadius={75}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  animationDuration={1000}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <PercentageNumber>{total}%</PercentageNumber>
            <CenterLabel>{userLanguage === 'es' ? 'Total' : 'Total'}</CenterLabel>
          </PieContainer>
        </Col>
        
        <Col lg={7}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <NormalFormNumberField
                label={userTypeMapper[UserTypeOption.Tenant][userLanguage]}
                name="tenant"
                value={responsibilities[UserTypeOption.Tenant].toString()}
                onChange={(val) => handleChange(UserTypeOption.Tenant, val)}
              />
              <NormalFormNumberField
                label={userTypeMapper[UserTypeOption.Owner][userLanguage]}
                name="owner"
                value={responsibilities[UserTypeOption.Owner].toString()}
                onChange={(val) => handleChange(UserTypeOption.Owner, val)}
              />
              <NormalFormNumberField
                label={userTypeMapper[UserTypeOption.Manager][userLanguage]}
                name="manager"
                value={responsibilities[UserTypeOption.Manager].toString()}
                onChange={(val) => handleChange(UserTypeOption.Manager, val)}
              />
              <NormalFormNumberField
                label={userTypeMapper[UserTypeOption.Supplier][userLanguage]}
                name="supplier"
                value={responsibilities[UserTypeOption.Supplier].toString()}
                onChange={(val) => handleChange(UserTypeOption.Supplier, val)}
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 font-medium text-center">{error}</p>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleUpdate}
                disabled={total !== 100 || isLoading}
                isLoading={isLoading}
              >
                {userLanguage === 'es' ? 'Actualizar Responsabilidad' : 'Update Responsibility'}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
              {Object.entries(userTypeMapper).map(([type, labels], index) => (
                <div key={type} className="flex items-center">
                  <ColorDot $color={COLORS[index]} />
                  <span className="text-xs text-gray-600">{labels[userLanguage]}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </ChartContainer>
  );
};
